import { createContext, useContext, useState, Fragment, useRef, useCallback, useEffect } from 'react';
import useDownloadProgress from 'hooks/useDownloadProgress';
import { autoDownloadPresignedUrl, sanitizeFileName } from 'utils/presignedDownloadUtils';
import { processChronologyExhibitDownloadMessage, CHRONOLOGY_EXHIBITS_DOWNLOAD_TYPE } from 'utils/chronologyExhibitDownloadWs';
import useNotifications from 'hooks/useNotifications';
import useClientNoification from 'hooks/useClientNoification';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import useMultipartUpload from 'hooks/useMultipartUpload';
import UploadWindow from 'components/UploadWindow/UploadWindow';
import DownloadTray from 'components/DownloadTray/DownloadTray';
import { useWebSocketContext } from './WebSocketConnectionContext';
import { postActivityLog } from 'services/generalServices';
import { getTenantDetails } from 'utils/utils';

export const NavBarContext = createContext();

export const useNavbarContext = () => {
  return useContext(NavBarContext);
};

export const NavBarContextProvider = ({ children }) => {
  const [uploadWindowMinimized, setUploadWindowMinimized] = useState(false);
  const { downloads, downloadFile, remove, setDownloads } = useDownloadProgress();
  const notificationData = useNotifications();
  const { readFirmNotifications } = notificationData || {};
  const { getNotificationCounts, notificationCounts, readClientNotifications } = useClientNoification();
  const { startUpload, uploadingFiles, pauseUpload, resumeUpload, cancelAllUploads, cancelUpload } = useMultipartUpload();
  const { subscribe, unsubscribe, caseNotePDFDownloadData } = useWebSocketContext();
  const confirmDialogRef = useRef(null);
  const [textMessageFilterActive, setTextMessageFilterActive] = useState(false);

  // Memoize the setter to prevent infinite loops
  const handleSetTextMessageFilterActive = useCallback((value) => {
    setTextMessageFilterActive(value);
  }, []);

  const [folderDownloads, setFolderDownloads] = useState([]);
  const [isTrayVisible, setIsTrayVisible] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({});
  const folderDownloadsRef = useRef([]);
  const activeDownloadsRef = useRef([]);
  const processedFolderDownloadSksRef = useRef(new Set());

  const findActiveFolderDownloadId = useCallback((case_id, file_name) => {
    const activeDownloads = activeDownloadsRef.current ?? [];

    const inProgressCaseDownload = [...activeDownloads]
      .reverse()
      .find((download) => download.type === 'download_case' && download.caseId === case_id && download.status === 'In Progress');

    if (inProgressCaseDownload) {
      return inProgressCaseDownload.id;
    }

    return activeDownloads.find((download) => {
      if (download.caseId !== case_id || download.type === 'download_case') {
        return false;
      }

      return file_name ? download.folderName === file_name.split('_')[0] : true;
    })?.id;
  }, []);

  const showConfirmDialog = useCallback((content) => {
    return new Promise((resolve, reject) => {
      if (confirmDialogRef.current) {
        confirmDialogRef.current.open(content);
        const handleConfirm = (data) => {
          resolve(data);
        };
        const handleCancel = () => {
          reject(false);
        };
        confirmDialogRef.current.open(content, handleConfirm, handleCancel);
      }
    });
  }, []);

  const toggleUploadWindow = () => {
    setUploadWindowMinimized(!uploadWindowMinimized);
  };

  // Folder download functions
  const addFolderDownload = useCallback((download) => {
    setFolderDownloads((prev) => {
      const exists = prev.find((d) => d.id === download.id);
      const newDownloads = exists ? prev.map((d) => (d.id === download.id ? { ...d, ...download } : d)) : [...prev, download];
      folderDownloadsRef.current = newDownloads;
      return newDownloads;
    });

    const existingIndex = activeDownloadsRef.current?.findIndex((d) => d.id === download.id);
    if (existingIndex >= 0) {
      activeDownloadsRef.current = activeDownloadsRef.current.map((d) => (d.id === download.id ? { ...d, ...download } : d));
    } else {
      activeDownloadsRef.current = [...activeDownloadsRef.current, download];
    }

    setIsTrayVisible(true);
  }, []);

  const updateFolderDownload = useCallback((id, updates) => {
    setFolderDownloads((prev) => {
      const newDownloads = prev.map((download) => (download.id === id ? { ...download, ...updates } : download));
      folderDownloadsRef.current = newDownloads;
      return newDownloads;
    });
  }, []);

  const removeFolderDownload = useCallback((id) => {
    setFolderDownloads((prev) => {
      const newDownloads = prev.filter((download) => download.id !== id);
      folderDownloadsRef.current = newDownloads;
      if (newDownloads.length === 0) {
        setIsTrayVisible(false);
      }
      return newDownloads;
    });
  }, []);

  const removeZipFile = useCallback((downloadId, zipFileId) => {
    setFolderDownloads((prev) => {
      const newDownloads = prev.map((download) => {
        if (download.id === downloadId) {
          // Remove the zipFile if it matches the zipFileId
          const hasMatchingZipFile = download.zipFile?.id === zipFileId;
          return {
            ...download,
            zipFile: hasMatchingZipFile ? null : download.zipFile,
          };
        }
        return download;
      });

      const filteredDownloads = newDownloads?.filter((download) => {
        const hasZipFile = download.zipFile && download.zipFile.id;
        const isInProgress = download.status === 'In Progress';
        return hasZipFile || isInProgress;
      });

      folderDownloadsRef.current = filteredDownloads;

      if (filteredDownloads.length === 0) {
        setIsTrayVisible(false);
      }

      return filteredDownloads;
    });
  }, []);

  const closeTray = useCallback(() => {
    // Only clear UI state, keeping downloads running in background
    setFolderDownloads([]);
    folderDownloadsRef.current = [];
    setIsTrayVisible(false);
    setDownloads([]);
  }, []);

  const completeFolderDownload = useCallback(
    (downloadId) => {
      updateFolderDownload(downloadId, { status: 'done' });
      setDownloadProgress((prev) => {
        const newState = { ...prev };
        delete newState[downloadId];
        return newState;
      });

      const completedFolder = activeDownloadsRef.current?.find((d) => d.id === downloadId);
      if (completedFolder && completedFolder.type !== CHRONOLOGY_EXHIBITS_DOWNLOAD_TYPE) {
        const [tenantId] = getTenantDetails();
        const isCaseDownload = completedFolder.type === 'download_case';
        postActivityLog({
          event_key: 'Download',
          event_type: 'Files',
          event_subtype: isCaseDownload ? 'download_case' : 'download_folder',
          message: isCaseDownload ? `Downloaded Case: ${completedFolder.folderName}` : `Downloaded Folder: ${completedFolder.folderName}`,
          event_status: 'success',
          pk: completedFolder.pk,
          sk: completedFolder.sk,
          event_items: {
            case_id: completedFolder.caseId ?? '',
            case_name: completedFolder.case_name ?? '',
            is_lead: completedFolder.is_lead,
            tenant_id: tenantId,
          },
        }).catch((err) => {
          console.error('Error posting folder download activity log:', err);
        });
      }

      activeDownloadsRef.current = activeDownloadsRef.current?.filter((d) => d.id !== downloadId);

      setTimeout(() => {
        updateFolderDownload(downloadId, { isDismissing: true });
      }, 1700);
      setTimeout(() => {
        removeFolderDownload(downloadId);
      }, 2000);
    },
    [updateFolderDownload, removeFolderDownload]
  );

  const handleFolderContentMessage = useCallback(
    (data) => {
      if (data?.notification_type !== 'get_folder_content_message') {
        return;
      }

      const messageSk = data?.sk;
      if (messageSk && processedFolderDownloadSksRef.current.has(messageSk)) {
        return;
      }

      const folderContent = data.body?.['folder-content'];
      if (!folderContent) {
        return;
      }

      const { case_id, task_status, message, presigned_url, file_name: rawFileName } = folderContent;
      const file_name = sanitizeFileName(rawFileName);
      const downloadId = findActiveFolderDownloadId(case_id, file_name);

      if (!downloadId) {
        return;
      }

      if (messageSk) {
        processedFolderDownloadSksRef.current.add(messageSk);
      }

      if (task_status === 'Success') {
        updateFolderDownload(downloadId, {
          status: 'completed',
          zipFile: {
            id: downloadId,
            fileName: file_name,
            presignedUrl: presigned_url,
          },
          message: message,
        });

        if (presigned_url && autoDownloadPresignedUrl(presigned_url)) {
          completeFolderDownload(downloadId);
        } else {
          updateFolderDownload(downloadId, {
            status: 'failed',
            error: message || 'Download failed',
          });
          activeDownloadsRef.current = activeDownloadsRef.current?.filter((d) => d.id !== downloadId);
        }
      } else if (task_status === 'Failed') {
        updateFolderDownload(downloadId, {
          status: 'failed',
          error: message || 'Download failed',
        });
        activeDownloadsRef.current = activeDownloadsRef.current?.filter((d) => d.id !== downloadId);
      }
    },
    [findActiveFolderDownloadId, completeFolderDownload, updateFolderDownload]
  );

  const handleChronologyExhibitContentMessage = useCallback(
    (data) => {
      processChronologyExhibitDownloadMessage({
        data,
        activeDownloads: activeDownloadsRef.current,
        processedSks: processedFolderDownloadSksRef.current,
        updateFolderDownload,
        completeFolderDownload,
        removeActiveDownload: (downloadId) => {
          activeDownloadsRef.current = activeDownloadsRef.current?.filter((d) => d.id !== downloadId);
        },
      });
    },
    [completeFolderDownload, updateFolderDownload]
  );

  useEffect(() => {
    if (!subscribe) {
      return undefined;
    }

    subscribe('ALL', handleFolderContentMessage);
    subscribe('ALL', handleChronologyExhibitContentMessage);
    return () => {
      unsubscribe?.('ALL', handleFolderContentMessage);
      unsubscribe?.('ALL', handleChronologyExhibitContentMessage);
    };
  }, [subscribe, unsubscribe, handleFolderContentMessage, handleChronologyExhibitContentMessage]);

  // Handle WebSocket messages for case note PDF downloads
  useEffect(() => {
    if (caseNotePDFDownloadData) {
      const data = caseNotePDFDownloadData;
      if (data?.notification_type === 'get_case_pdf_message') {
        const pdfContent = data.body;
        if (pdfContent) {
          const { case_id, task_status, message, presigned_url, file_name } = pdfContent;
          const downloadId = activeDownloadsRef.current?.find((download) => download.caseId === case_id)?.id;
          if (downloadId) {
            if (task_status === 'Success') {
              updateFolderDownload(downloadId, {
                status: 'completed',
                zipFile: {
                  id: downloadId,
                  fileName: file_name,
                  presignedUrl: presigned_url,
                },
                message: message,
              });

              if (presigned_url && file_name) {
                downloadFile(
                  presigned_url,
                  file_name,
                  () => {
                    // Transition PDF card to done phase, then auto-dismiss with animation
                    updateFolderDownload(downloadId, { status: 'done' });
                    setDownloadProgress((prev) => {
                      const newState = { ...prev };
                      delete newState[downloadId];
                      return newState;
                    });
                    activeDownloadsRef.current = activeDownloadsRef.current?.filter((d) => d.id !== downloadId);

                    setTimeout(() => {
                      updateFolderDownload(downloadId, { isDismissing: true });
                    }, 1700);
                    setTimeout(() => {
                      removeFolderDownload(downloadId);
                    }, 2000);
                  },
                  (progress, loaded, total) => {
                    setDownloadProgress((prev) => ({
                      ...prev,
                      [downloadId]: {
                        progress,
                        loaded,
                        total,
                        fileName: file_name,
                        isDownloading: true,
                      },
                    }));
                  },
                  false
                );
              }
            } else if (task_status === 'Failed') {
              updateFolderDownload(downloadId, {
                status: 'failed',
                error: message || 'PDF download failed',
              });
              activeDownloadsRef.current = activeDownloadsRef.current?.filter((d) => d.id !== downloadId);
            }
          }
        }
      }
    }
  }, [caseNotePDFDownloadData]);

  const value = {
    notificationCounts,
    getNotificationCounts,
    readClientNotifications,
    readFirmNotifications,
    downloadFile,
    notificationData,
    showConfirmDialog,
    startUpload,
    uploadingFiles,
    pauseUpload,
    resumeUpload,
    folderDownloads,
    isTrayVisible,
    addFolderDownload,
    updateFolderDownload,
    removeFolderDownload,
    removeZipFile,
    closeTray,
    setIsTrayVisible,
    downloadProgress,
    fileDownloads: downloads,
    removeFileDownload: remove,
    textMessageFilterActive,
    setTextMessageFilterActive: handleSetTextMessageFilterActive,
  };

  return (
    <NavBarContext.Provider value={value}>
      {children}
      <ConfirmDialog ref={confirmDialogRef} />
      {Object.values(uploadingFiles).some((file) => !file.completed) && (
        <UploadWindow
          files={uploadingFiles}
          onPause={pauseUpload}
          onResume={resumeUpload}
          onCancel={cancelAllUploads}
          onMinimize={toggleUploadWindow}
          minimized={uploadWindowMinimized}
          onFileCancel={cancelUpload}
        />
      )}
      {(isTrayVisible || downloads.length > 0 || Object.keys(downloadProgress)?.length > 0) && (
        <DownloadTray
          folderDownloads={folderDownloads}
          fileDownloads={downloads}
          downloadProgress={downloadProgress}
          onClose={closeTray}
          onRemoveDownload={removeFolderDownload}
          onRemoveZipFile={removeZipFile}
          onRemoveFileDownload={remove}
        />
      )}
    </NavBarContext.Provider>
  );
};
